import { Card, CardContent } from "@/components/ui/card";
import { Clock, Timer, TrendingDown, TrendingUp, CheckCircle2, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { CycleTimeKPIs } from "@/hooks/useCycleTimeAnalytics";
import { Skeleton } from "@/components/ui/skeleton";

interface CycleTimeKPICardsProps {
  kpis: CycleTimeKPIs | null;
  isLoading: boolean;
}

interface KPICardProps {
  title: string;
  value: string;
  subtitle: string;
  trend: number;
  trendLabel: string;
  icon: React.ReactNode;
  invertTrend?: boolean; // For cycle times, lower is better
  isLoading: boolean;
}

function KPICard({ 
  title, 
  value, 
  subtitle, 
  trend, 
  trendLabel, 
  icon, 
  invertTrend = false,
  isLoading 
}: KPICardProps) {
  // For cycle times, negative trend (getting faster) is good
  // For job count, positive trend (more jobs) is good
  const isPositive = invertTrend ? trend < 0 : trend > 0;
  const isNegative = invertTrend ? trend > 0 : trend < 0;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
            <Skeleton className="h-10 w-10 rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold tracking-tight">{value}</p>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
            {trend !== 0 && (
              <div className={cn(
                "flex items-center gap-1 text-xs font-medium mt-2",
                isPositive && "text-emerald-600 dark:text-emerald-400",
                isNegative && "text-amber-600 dark:text-amber-400",
                !isPositive && !isNegative && "text-muted-foreground"
              )}>
                {isPositive ? (
                  <TrendingDown className="h-3 w-3" />
                ) : isNegative ? (
                  <TrendingUp className="h-3 w-3" />
                ) : null}
                <span>
                  {Math.abs(trend).toFixed(1)}% {trendLabel}
                </span>
              </div>
            )}
          </div>
          <div className="p-2.5 rounded-lg bg-primary/10">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function CycleTimeKPICards({ kpis, isLoading }: CycleTimeKPICardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <KPICard
        title="Avg Total Cycle Time"
        value={kpis ? `${kpis.avgTotalCycleTime.toFixed(1)} days` : "—"}
        subtitle={kpis ? `Median: ${kpis.medianTotalCycleTime.toFixed(1)} days` : ""}
        trend={kpis?.trendVsPreviousPeriod.totalCycleTime ?? 0}
        trendLabel="vs previous"
        icon={<Clock className="h-5 w-5 text-primary" />}
        invertTrend={true}
        isLoading={isLoading}
      />

      <KPICard
        title="Avg Time to Award"
        value={kpis ? `${kpis.avgTimeToAward.toFixed(1)} days` : "—"}
        subtitle={kpis ? `Across ${kpis.jobsCompleted} jobs` : ""}
        trend={kpis?.trendVsPreviousPeriod.timeToAward ?? 0}
        trendLabel="vs previous"
        icon={<Timer className="h-5 w-5 text-primary" />}
        invertTrend={true}
        isLoading={isLoading}
      />

      <KPICard
        title="Avg Production Time"
        value={kpis ? `${kpis.avgProductionTime.toFixed(1)} days` : "—"}
        subtitle={kpis ? `Median: ${kpis.medianProductionTime.toFixed(1)} days` : ""}
        trend={kpis?.trendVsPreviousPeriod.productionTime ?? 0}
        trendLabel="vs previous"
        icon={<Zap className="h-5 w-5 text-primary" />}
        invertTrend={true}
        isLoading={isLoading}
      />

      <KPICard
        title="Jobs Completed"
        value={kpis ? kpis.jobsCompleted.toString() : "—"}
        subtitle={kpis ? `${kpis.avgJobsPerWeek.toFixed(1)} per week` : ""}
        trend={kpis?.trendVsPreviousPeriod.jobsCompleted ?? 0}
        trendLabel="vs previous"
        icon={<CheckCircle2 className="h-5 w-5 text-primary" />}
        invertTrend={false}
        isLoading={isLoading}
      />
    </div>
  );
}
